/**
 * Leader Scoring Adapter (DKPLAT-354)
 *
 * Transforms scoring question data into LeaderScoringQuestion component props
 * for both `player_leader` and `team_leader` question types.
 *
 * Leader questions are cross-fixture: there's no single match feed to read
 * from, so the per-candidate stat value lives on each option's grading_data.
 * The live grader writes `liveValue` by summing the stat across ONLY the
 * pickem's selected fixtures, so on the scoring screen we surface that value —
 * i.e. goals scored in the contest fixtures, which is 0 before kick-off. We do
 * NOT fall back to the candidate's `seasonValue` (season-to-date total captured
 * at admin save time): showing season form here misrepresents it as contest
 * progress (DKPLAT-442).
 */

import type { ScoringQuestionData } from '../types';

/**
 * Shape of the leader-specific fields stored on an option's grading_data.
 * Several of these (seasonValue, teamShortcode, fixtureLabel, matchTime) are
 * written by LeaderQuestionSection but aren't part of the generated
 * grading_data type, so we read them through this local view.
 */
interface LeaderGradingData {
  playerId?: number | string;
  teamId?: number | string;
  playerPosition?: string;
  teamShortcode?: string;
  fixtureLabel?: string;
  matchTime?: string;
  liveValue?: number;
  seasonValue?: number;
}

export interface LeaderScoringCandidate {
  option_id?: string;
  name: string;
  imageUrl?: string | null;
  teamLogoUrl?: string | null;
  position?: string;
  teamShortcode?: string;
  fixtureLabel?: string;
  is_correct: boolean;
  statValue: number;
  // Entity + stat identifiers needed to open the QuickStatOverrideDialog when
  // manual scoring is on. Exactly one of playerId / teamId is set per candidate.
  playerId?: string;
  teamId?: string;
  statType?: string;
}

export interface LeaderScoringQuestionProps {
  questionText: string;
  questionSubtext?: string;
  questionNumber?: number;
  kind: 'player' | 'team';
  statLabel: string;
  options: LeaderScoringCandidate[];
  points: number;
  status?: 'LIVE' | 'FINAL' | 'SCHEDULED';
}

/**
 * Transforms scoring data into LeaderScoringQuestion props
 *
 * @param data - Scoring question data
 * @returns LeaderScoringQuestion component props
 */
export function leaderScoringAdapter(data: ScoringQuestionData): LeaderScoringQuestionProps {
  const meta = data.leaderboard ?? {};
  // Prefer the explicit kind on the metadata; fall back to inferring from the
  // question type so questions saved before kind was persisted still render.
  const kind: 'player' | 'team' =
    meta.kind === 'team' || data.questionType === 'team_leader' ? 'team' : 'player';

  const statLabel = meta.statLabel || meta.statType || data.statTypeCode || 'Stat';
  // The raw stat code (not the human label) is what the grader keys overrides
  // off, so pass it through for the QuickStatOverrideDialog.
  const statType = meta.statType || data.statTypeCode || undefined;

  const options: LeaderScoringCandidate[] = data.options.map(opt => {
    const grading = (opt.grading_data ?? {}) as LeaderGradingData;
    // Goals accumulated across the pickem's selected fixtures, written by the
    // live grader as `liveValue`. Pre-game (before the grader runs) this is
    // absent — show 0, NOT the candidate's season-to-date total (`seasonValue`),
    // since this column represents contest-fixture progress, not season form.
    const statValue = typeof grading.liveValue === 'number' ? grading.liveValue : 0;

    return {
      option_id: opt.option_id,
      name: opt.option_text_en || 'Unknown',
      imageUrl: opt.option_image_en,
      teamLogoUrl: opt.third_image_en,
      position: grading.playerPosition,
      teamShortcode: grading.teamShortcode,
      fixtureLabel: grading.fixtureLabel,
      is_correct: opt.is_correct,
      statValue,
      playerId: grading.playerId != null ? String(grading.playerId) : undefined,
      teamId: grading.teamId != null ? String(grading.teamId) : undefined,
      statType,
    };
  });

  // Rank by stat value desc so the current leader sits at the top, mirroring
  // the customer-facing LeaderPick ordering.
  options.sort((a, b) => b.statValue - a.statValue);

  return {
    questionText: data.questionText,
    questionSubtext: data.questionSubtext,
    questionNumber: data.questionNumber,
    kind,
    statLabel,
    options,
    points: data.totalPoints,
    status: data.status,
  };
}
